<template>
  <rest-page :fetch-endpoint="fetchEndpoint" :layout="viewPage ? 'tabbed' : ''">
    <template v-if="model">
      <page-header-new :breadcrumbs="breadcrumbs" :title="title">
        <template slot="actions">
          <el-button
            v-if="updatePage"
            type="primary"
            plain
            @click="cancelUpdate()"
            icon="el-icon-close"
            data-qa="discard-button"
          >
            {{ $t('Button_Discard') }}
          </el-button>

          <segment-evaluate-dropdown :segment="model" />

          <segment-settings-dropdown
            v-if="viewPage && !readonly"
            :segment="model"
          />

          <el-button
            v-if="createPage || updatePage || copyPage"
            type="primary"
            @click="save()"
            icon="ico icon-floppy"
            data-qa="save-button"
          >
            {{ $t('Button_Save') }}
          </el-button>
        </template>

        <template slot="content">
          <segment-counts-top-bar
            v-if="segmentMetrics"
            :metrics="segmentMetrics"
            :type="segmentMetricsType"
          />
        </template>
      </page-header-new>

      <page-tabs v-if="viewPage" :tabs="tabs"></page-tabs>

      <page-content v-if="createPage || updatePage || copyPage">
        <form-section>
          <rest-form
            ref="form"
            :save-endpoint="saveEndpoint"
            :after-save="afterSave"
            :model="model"
            :rules="rules"
            :submit-native="false"
          >
            <form-item
              style="margin-bottom: 0"
              :label="$t('Audience_Segment_Form_Title')"
              :hint="$t('Audience_Segment_Form_Title_Hint')"
            >
              <el-input v-model="model.title" data-qa="segment-name-input" />
            </form-item>
          </rest-form>
        </form-section>

        <form-section style="overflow: visible">
          <conditions-editor
            ref="conditions-editor"
            :id="segmentId"
            :conditions="model.conditions"
            v-model="model.conditions"
            data-model-name="Segment"
            data-model-attribute="conditions"
            attribute-list-data-url="/segment/segment/attribute-list-data"
            :preview="false"
          />
        </form-section>
      </page-content>

      <calculate-size-dialog v-if="showCalculateSizeDialog" />
      <channel-reach-dialog v-if="showChannelsReachDialog" />
      <channel-reach-configure-dialog v-if="showChannelsReachConfigureDialog" />
      <members-preview-loading-dialog v-if="showPreviewMembersDialog" />
      <members-preview-list-dialog
        v-if="showPreviewMembersListDialog"
        :profile-ids="members_preview_result.task.recipients"
      />
      <members-export-dialog v-if="showExportMembersDialog" />
      <members-export-run-dialog
        v-if="showExportMembersRunDialog"
        :task="export_result"
      />
      <members-export-add-attributes-dialog
        v-if="showExportMembersAttributesDialog"
      />
    </template>
  </rest-page>
</template>

<script>
import { projectEndpoint, projectRoute } from '@/core/helpers'
import { EventBus } from '@/core/EventBus'
import RestApi from '@/core/services/RestApi'
import PageHeaderNew from '@/common/components/page/PageHeaderNew'
import PageContent from '@/common/components/page/PageContent'
import SegmentEvaluateDropdown from '@/modules/audiences/components/SegmentEvaluateDropdown'
import RestPage from '@/common/components/rest/RestPage'
import RestForm from '@/common/components/rest/RestForm'
import ConditionsEditor from '@/common/components/ConditionsEditor'
import FormItem from '@/common/components/form/FormItem'
import FormSection from '@/common/components/form/FormSection'
import SegmentSettingsDropdown from '@/modules/audiences/components/SegmentSettingsDropdown'
import SegmentCountsTopBar from '@/modules/audiences/components/SegmentCountsTopBar'
import ChannelReachDialog from '@/modules/audiences/components/ChannelReachDialog'
import MembersPreviewListDialog from '@/modules/audiences/components/MembersPreviewListDialog'
import CalculateSizeDialog from '@/modules/audiences/components/CalculateSizeDialog'
import MembersPreviewLoadingDialog from '@/modules/audiences/components/MembersPreviewLoadingDialog'
import MembersExportDialog from '@/modules/audiences/components/MembersExportDialog'
import MembersExportRunDialog from '@/modules/audiences/components/MembersExportRunDialog'
import MembersExportAddAttributesDialog from '@/modules/audiences/components/MembersExportAddAttributesDialog'
import PageTabs from '@/common/components/page/PageTabs'
import ChannelReachConfigureDialog from '../components/ChannelReachConfigureDialog'
import iframeTitleMixin from '@/common/mixins/iframeTitleMixin'

export default {
  name: 'SegmentPage',

  mixins: [iframeTitleMixin],

  components: {
    ChannelReachConfigureDialog,
    FormSection,
    FormItem,
    ConditionsEditor,
    RestForm,
    RestPage,
    PageContent,
    SegmentCountsTopBar,
    ChannelReachDialog,
    MembersPreviewListDialog,
    CalculateSizeDialog,
    MembersPreviewLoadingDialog,
    MembersExportDialog,
    MembersExportRunDialog,
    MembersExportAddAttributesDialog,
    SegmentEvaluateDropdown,
    SegmentSettingsDropdown,
    PageTabs,
    PageHeaderNew
  },

  data() {
    return {
      model: null,
      showCalculateSizeDialog: false,
      showPreviewMembersDialog: false,
      showPreviewMembersListDialog: false,
      showChannelsReachDialog: false,
      showChannelsReachConfigureDialog: false,
      showExportMembersDialog: false,
      showExportMembersRunDialog: false,
      showExportMembersAttributesDialog: false,
      channelsReachParams: {},
      exportParams: {},
      taskId: null,
      calculate_size_result: {},
      channel_reach_result: {},
      export_result: {},
      members_preview_result: null,
      taskLoop: null,
      segmentMetrics: null,
      segmentMetricsType: null
    }
  },

  methods: {
    save() {
      if (this.copyPage) {
        this.model.copy = true
      }

      const conditionsEditor = this.$refs['conditions-editor']
      if (conditionsEditor) {
        const validationErrors = conditionsEditor.validateConditions()
        if (validationErrors && validationErrors.length > 0) {
          const errorMessage = validationErrors
            .map(
              error =>
                error.message || this.$t('Audience_Segment_Validation_Error')
            )
            .join('<br><br>')
          this.$message({
            dangerouslyUseHTMLString: true,
            message: errorMessage,
            type: 'error'
          })
          return
        }
      }

      this.model.conditions = conditionsEditor.serialize()
      this.$refs['form'].save()
    },

    afterSave(model) {
      this.$router.push(
        projectRoute({
          name: 'AudienceSegmentOverview',
          query: { id: model.id }
        })
      )
    },

    cancelUpdate() {
      this.$router.push(
        projectRoute({
          name: 'AudienceSegmentOverview',
          query: { id: this.model.id }
        })
      )
    },

    calculateSize(scope, samplingAllowed) {
      let evaluateData = this.evaluateData()
      evaluateData.scope = scope || 'all'
      evaluateData.samplingAllowed = !!samplingAllowed
      RestApi.post(this.countEndpoint.url, evaluateData)
        .then(({ data }) => {
          this.taskId = data.task_id
          this.fetchSizeTask()
          this.taskLoop = setInterval(() => {
            this.fetchSizeTask()
          }, 2000)
        })
        .catch(error => {
          this.showCalculateSizeDialog = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    calculateSizeShow(scope, samplingAllowed) {
      this.showCalculateSizeDialog = true
      this.calculateSize(scope, samplingAllowed)
    },

    calculateSizeHide() {
      this.showCalculateSizeDialog = false
    },

    calculateSizeCancel() {
      this.calculateSizeHide()
      clearInterval(this.taskLoop)
      this.deleteCurrentTask()
    },

    calculateSizeResult(scope, data) {
      this.segmentMetricsType = scope
      this.segmentMetrics = data
    },

    channelsReach(params, samplingAllowed) {
      let evaluateData = { ...this.evaluateData(), ...params }
      evaluateData.scope = 'channels'
      evaluateData.samplingAllowed = !!samplingAllowed
      RestApi.post(this.countEndpoint.url, evaluateData)
        .then(({ data }) => {
          this.taskId = data.task_id
          this.fetchReachTask()
          this.taskLoop = setInterval(() => {
            this.fetchReachTask()
          }, 2000)
        })
        .catch(error => {
          this.showChannelsReachDialog = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    channelsReachConfigure() {
      this.showChannelsReachConfigureDialog = true
    },

    channelsReachShow(params, samplingAllowed) {
      this.channelsReachParams = params
      this.showChannelsReachDialog = true
      this.showChannelsReachConfigureDialog = false
      this.channelsReach(params, samplingAllowed)
    },

    channelsReachHide() {
      this.showChannelsReachDialog = false
      this.showChannelsReachConfigureDialog = false
    },

    channelsReachCancel() {
      this.channelsReachHide()
      clearInterval(this.taskLoop)
      this.deleteCurrentTask()
    },

    channelsReachResult(data) {
      this.segmentMetricsType = 'channels'
      this.segmentMetrics = data
    },

    recalculateExactSize() {
      if (this.segmentMetricsType === 'channels') {
        this.channelsReachShow(this.channelsReachParams, false)
      } else {
        this.calculateSizeShow(this.segmentMetricsType, false)
      }
    },

    exportMembers() {
      let evaluateData = {
        ...this.evaluateData(),
        ...this.exportParams
      }
      RestApi.post(this.exportEndpoint.url, evaluateData)
        .then(({ data }) => {
          this.taskId = data.task_id
          this.fetchExportTask()
          this.taskLoop = setInterval(() => {
            this.fetchExportTask()
          }, 2000)
        })
        .catch(error => {
          this.showExportMembersDialog = false
          this.showExportMembersAttributesDialog = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    exportMembersShow() {
      this.showExportMembersDialog = true
    },

    exportMembersHide() {
      this.showExportMembersDialog = false
    },

    exportMembersCancel() {
      this.exportMembersHide()
      clearInterval(this.taskLoop)
      this.deleteCurrentTask()
    },

    exportMembersRunShow(params) {
      Object.assign(this.exportParams, params)
      this.showExportMembersDialog = false
      this.showExportMembersRunDialog = true
      this.exportMembers()
    },

    exportMembersRunHide() {
      this.showExportMembersRunDialog = false
      this.exportParams = {}
      this.export_result = {}
      clearInterval(this.taskLoop)
      this.taskId = null
    },

    exportMembersAddAttributesShow(params) {
      this.exportParams = params
      this.showExportMembersAttributesDialog = true
    },

    exportMembersAddAttributesHide() {
      this.showExportMembersAttributesDialog = false
    },

    membersPreview() {
      let evaluateData = this.evaluateData()
      evaluateData.limit = 25
      RestApi.post(this.previewEndpoint.url, evaluateData)
        .then(({ data }) => {
          if (this.showPreviewMembersDialog) {
            this.taskId = data.task_id
            this.fetchPreviewTask()
            this.taskLoop = setInterval(() => {
              this.fetchPreviewTask()
            }, 2000)
          }
        })
        .catch(error => {
          this.showPreviewMembersDialog = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    membersPreviewShow() {
      this.showPreviewMembersDialog = true
      this.membersPreview()
    },

    membersPreviewHide() {
      this.showPreviewMembersDialog = false
    },

    membersPreviewListClose() {
      this.showPreviewMembersListDialog = false
      this.members_preview_result = null
    },

    membersPreviewListShow() {
      this.showPreviewMembersListDialog = true
    },

    membersPreviewCancel() {
      this.membersPreviewHide()
      clearInterval(this.taskLoop)
      this.deleteCurrentTask()
    },

    fetchSizeTask() {
      RestApi.get(this.taskEndpoint.url).then(({ data }) => {
        this.calculate_size_result = data
      })
    },

    fetchReachTask() {
      RestApi.get(this.taskEndpoint.url).then(({ data }) => {
        this.channel_reach_result = data
      })
    },

    fetchPreviewTask() {
      RestApi.get(this.taskEndpoint.url).then(({ data }) => {
        this.members_preview_result = data
      })
    },

    fetchExportTask() {
      RestApi.get(this.taskEndpoint.url).then(({ data }) => {
        this.export_result = data
      })
    },

    deleteCurrentTask() {
      RestApi.delete(this.taskEndpoint.url)
    },

    evaluateData() {
      if (this.$refs['conditions-editor']) {
        return {
          conditions: this.$refs['conditions-editor'].serialize(),
          id: this.segmentId
        }
      } else {
        return {
          conditions: this.model.conditions,
          id: this.segmentId
        }
      }
    },

    handleTaskStatus(task, success, error) {
      switch (task.status) {
        case 2:
          clearInterval(this.taskLoop)
          success()
          break

        case 3:
          this.$message({
            message: this.$t('Audiences_Segment_Alert_Error'),
            type: 'error'
          })
          clearInterval(this.taskLoop)
          error()
          break

        case 4:
          this.$message({
            message: this.$t('Audiences_Segment_Alert_Fatal'),
            type: 'error'
          })
          clearInterval(this.taskLoop)
          error()
          break
      }
    }
  },

  created() {
    if (!this.segmentId) {
      this.model = {
        conditions: {
          operator: 'AND',
          '0': {
            operator: 'AND',
            '0': {}
          }
        }
      }
    }
  },

  computed: {
    segmentId() {
      return this.$route.query.id
    },

    updatePage() {
      return this.$route.name === 'AudienceSegmentUpdate'
    },

    createPage() {
      return this.$route.name === 'AudienceSegmentCreate'
    },

    copyPage() {
      return this.$route.name === 'AudienceSegmentCopy'
    },

    viewPage() {
      return (
        this.currentRoute === 'AudienceSegmentOverview' ||
        this.currentRoute === 'AudienceSegmentEvaluations' ||
        this.currentRoute === 'AudienceSegmentSharing'
      )
    },

    fetchEndpoint() {
      if (this.segmentId) {
        return projectEndpoint({
          method: 'get',
          url: `segments/${this.segmentId}`,
          handler: data => {
            this.model = data

            if (this.copyPage) {
              this.model.title += ' ' + this.$t('Audiences_Segment_Label_Copy')
            }
          }
        })
      } else {
        return null
      }
    },

    saveEndpoint() {
      if (this.segmentId && !this.copyPage) {
        return projectEndpoint({
          method: 'patch',
          url: `segments/${this.segmentId}`
        })
      } else {
        return projectEndpoint({
          method: 'post',
          url: `segments`
        })
      }
    },

    countEndpoint() {
      return projectEndpoint({
        url: `segment-evaluate/count`
      })
    },

    previewEndpoint() {
      return projectEndpoint({
        url: `segment-evaluate/preview`
      })
    },

    exportEndpoint() {
      return projectEndpoint({
        url: `segment-evaluate/export`
      })
    },

    taskEndpoint() {
      return projectEndpoint({
        url: `task-histories/${this.taskId}?expand=task`
      })
    },

    rules() {
      return {
        title: {
          required: true
        }
      }
    },

    currentRoute() {
      return this.$router.currentRoute.name
    },

    title() {
      if (this.createPage) {
        return this.$t('Audience_Segment_Create_Title')
      }
      return this.model ? this.model.title : null
    },

    breadcrumbs() {
      return [
        {
          label: 'Data'
        },
        {
          label: this.$t('Audiences_Breadcrumb_Audiences'),
          route: projectRoute({
            name: 'AudienceSegmentList'
          })
        },
        {
          label: this.createPage
            ? this.$t('Audiences_Breadcrumb_CreateSegment')
            : this.$t('Audiences_Breadcrumb_Segment')
        }
      ]
    },

    tabs() {
      const sharingTab = {
        title: this.$t('Audiences_SegmentSharingTab'),
        route: projectRoute({
          name: 'AudienceSegmentSharing',
          query: {
            id: this.$route.query.id
          }
        })
      }

      let adminTabs = []

      if (
        this.$store.state.app.project.company.segment_sharing &&
        this.$store.state.app.project.company.projects_count > 1
      ) {
        adminTabs = [{ ...sharingTab }, ...adminTabs]
      }

      return [
        {
          title: this.$t('Audience_Segment_Overview_Tab'),
          route: projectRoute({
            name: 'AudienceSegmentOverview',
            query: {
              id: this.$route.query.id
            }
          })
        },
        {
          title: this.$t('Audiences_SegmentEvaluationsTab'),
          route: projectRoute({
            name: 'AudienceSegmentEvaluations',
            query: {
              id: this.$route.query.id
            }
          })
        },
        ...(this.$can('manageProjectTechnical') && adminTabs)
      ]
    },

    readonly() {
      return !this.$can('Segment::Update')
    }
  },

  watch: {
    calculate_size_result: {
      handler(result) {
        if (result) {
          this.handleTaskStatus(
            result,
            () => {
              EventBus.$emit(
                'segment-metrics-size-change',
                result.task.scope,
                result.task.count
              )
              this.calculateSizeHide()
            },
            () => {
              this.calculateSizeHide()
            }
          )
        }
      },
      deep: true
    },

    channel_reach_result: {
      handler(result) {
        this.handleTaskStatus(
          result,
          () => {
            EventBus.$emit('segment-metrics-channel-change', result.task.count)
            this.channelsReachHide()
          },
          () => {
            this.channelsReachHide()
          }
        )
      },
      deep: true
    },

    members_preview_result: {
      handler(result) {
        if (!result) {
          return
        }
        this.handleTaskStatus(
          result,
          () => {
            this.membersPreviewHide()
            this.membersPreviewListShow()
          },
          () => {
            this.membersPreviewHide()
          }
        )
      },
      deep: true
    },

    export_result: {
      handler(result) {
        if (result) {
          this.handleTaskStatus(
            result,
            () => {},
            () => {
              this.exportMembersHide()
              this.exportMembersRunHide()
            }
          )
        }
      },
      deep: true
    }
  },

  mounted() {
    EventBus.$on('calculate-size-show', this.calculateSizeShow)
    EventBus.$on('calculate-size-hide', this.calculateSizeHide)
    EventBus.$on('calculate-size-cancel', this.calculateSizeCancel)
    EventBus.$on('channels-reach-configure', this.channelsReachConfigure)
    EventBus.$on('channels-reach-show', this.channelsReachShow)
    EventBus.$on('channels-reach-hide', this.channelsReachHide)
    EventBus.$on('channels-reach-cancel', this.channelsReachCancel)
    EventBus.$on('recalculate-exact-size', this.recalculateExactSize)
    EventBus.$on('members-preview-show', this.membersPreviewShow)
    EventBus.$on('members-preview-hide', this.membersPreviewHide)
    EventBus.$on('members-preview-list-close', this.membersPreviewListClose)
    EventBus.$on('members-preview-cancel', this.membersPreviewCancel)
    EventBus.$on('export-members-show', this.exportMembersShow)
    EventBus.$on('export-members-hide', this.exportMembersHide)
    EventBus.$on('export-members-run-show', this.exportMembersRunShow)
    EventBus.$on('export-members-run-hide', this.exportMembersRunHide)
    EventBus.$on('export-members-cancel', this.exportMembersCancel)
    EventBus.$on(
      'export-members-add-attributes-show',
      this.exportMembersAddAttributesShow
    )
    EventBus.$on(
      'export-members-add-attributes-hide',
      this.exportMembersAddAttributesHide
    )
    EventBus.$on('segment-metrics-size-change', this.calculateSizeResult)
    EventBus.$on('segment-metrics-channel-change', this.channelsReachResult)
  },

  destroyed() {
    EventBus.$off('calculate-size-show')
    EventBus.$off('calculate-size-hide')
    EventBus.$off('calculate-size-cancel')
    EventBus.$off('channels-reach-configure')
    EventBus.$off('channels-reach-show')
    EventBus.$off('channels-reach-hide')
    EventBus.$off('channels-reach-cancel')
    EventBus.$off('recalculate-exact-size')
    EventBus.$off('members-preview-show')
    EventBus.$off('members-preview-hide')
    EventBus.$off('members-preview-list-close')
    EventBus.$off('members-preview-cancel')
    EventBus.$off('export-members-show')
    EventBus.$off('export-members-hide')
    EventBus.$off('export-members-run-show')
    EventBus.$off('export-members-run-hide')
    EventBus.$off('export-members-add-attributes-show')
    EventBus.$off('export-members-add-attributes-hide')
    EventBus.$off('export-members-cancel')
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/audiences/pages/SegmentPage.vue